import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'qs';
import _ from 'lodash';
import { Select, Table, TableColumnsType } from 'antd';
import Loader from '../../../components/Loader';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Pagination from '../../../components/Pagination';
import Footer from '../../../components/layout/Footer';
import Button from '../../../components/Button';
import DownloadButton from '../../../components/DownloadButton';
import Checkbox from '../../../components/Checkbox';
import config from '../../../lib/config';
import exportTable from '../../../lib/exportTable';
import useAuth from '../../../hooks/useAuth';
import moment from 'moment';
import SelectEduStep from '../../../components/SelectEduStep';
import useAxios from '../../../hooks/useAxios';
import { IGetCourses, IGetSteps } from '../../../models/courses.model';
import { getCourses, getSteps } from '../../../api/courses.api';
import ErrorResponse from '../../../components/ErrorResponse';
import { getCommonCodeLabel } from '../../../lib/store';

const ProgramCourse: FC = () => {
  const { t, i18n } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const query = qs.parse(location.search, { ignoreQueryPrefix: true });
  const { data: res, isLoading, error, execute } = useAxios<IGetSteps>();  
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetCourses>();  

  const [courseData, setCourseData] = useState([]);
  const [page, setPage] = useState(1);
  const [param, setParam] = useState({});
  const [eduStepCode, setEduStepCode] = useState('');
  const [level, setLevel] = useState('');
  const [levels, setLevels] = useState<any>([]);
  const [step, setStep] = useState('');
  const [steps, setSteps] = useState<any>([]);
  const [allSteps, setAllSteps] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [isInUse, setIsInUse] = useState(false);
  const [exportTableLoading, setExportTableLoading] = useState(false);

  useEffect(() => {
    execute(getSteps());
    execute2(getCourses())
  }, []);

  useEffect(() => {
    if (res) {
      const newLevels: any = [];
      const newAllSteps: any = [];
      res.data?.forEach(level => {
        newLevels.push({
          label: level.courseName,
          value: level.courseCode,
          eduStepCode: level.eduStepCode,
        });
        level.step?.forEach(step => {
          newAllSteps.push({
            levelCode: level.courseCode,
            levelName: level.courseName,
            stepCode: step.courseCode,
            stepName: step.courseName,
          });
        })
      });
      setLevels(_.sortBy(newLevels, ['eduStepCode']));
      setSteps([]);
      setAllSteps(newAllSteps);
    }
  }, [res]);

  useEffect(() => {
    if (res2?.data && allSteps.length) {
      // query to state
      const _param: any = {};

      if (query.eduStepCode) {
        _param.eduStepCode = String(query.eduStepCode);
        setEduStepCode(_param.eduStepCode);
      }
      if (query.level) {
        _param.level = Number(query.level);
        setLevel(_param.level);
      }
      if (query.step) {
        _param.step = Number(query.step);
        setStep(_param.step);
      }
      if (query.keyword) {
        _param.keyword = String(query.keyword);
        setKeyword(_param.keyword);
      }
      if (query.isInUse) {
        _param.isInUse = query.isInUse === 'true';
        setIsInUse(_param.isInUse);
      }
      if (query.page) {
        _param.page = Number(query.page);
        setPage(_param.page);
      }

      setParam(_param);

      // data filtering
      let data: any = _.cloneDeep(res2.data);

      data.forEach((item: any)=> {
        const step: any = allSteps?.find((step: any) => step.stepCode === item.courseCode);
        if (step) {
          item.levelCode = step.levelCode;
          item.levelName = step.levelName;
          item.stepCode = step.stepCode;
          item.stepName = step.stepName;
        }
        item.curriculumType = item.curriculumType.trim();
      });

      if (_param.eduStepCode) {
        data = data.filter((item: any) => item.eduStepCode === _param.eduStepCode);
      }
      if (_param.level) {
        data = data.filter((item: any) => item.levelCode === _param.level);
      }
      if (_param.step) {
        data = data.filter((item: any) => item.stepCode === _param.step);
      }
      if (_param.keyword) {
        data = data.filter((item: any) => item.courseDetailName?.toLowerCase()?.includes(_param.keyword.toLowerCase()));
      }
      if (_param.isInUse) {
        data = data.filter((item: any) => item.useYn === 'Y');
      }

      setCourseData(data);
    }
  }, [location, allSteps, res2]);

  const onSetLevel = (value: string) => {
    setStep('');
    setLevel(value);

    if (value) {
      const level = res?.data?.find(item => item.courseCode === value);
      if (level) {
        const newSteps: any = [];
        level.step?.forEach(item => {
          newSteps.push({
            label: item.courseName,
            value: item.courseCode
          });
        });
        setSteps(newSteps);
      }
    }
  }

  const getLevelOptions = () => {
    if (eduStepCode) {
      return levels.filter((item: any) => item.eduStepCode === eduStepCode);
    }
    return levels;
  };

  const onSearch = () => {
    const param: any = { page: 1 };

    if (eduStepCode) {
      param.eduStepCode = eduStepCode;
    }
    if (level) {
      param.level = level;
    }
    if (step) {
      param.step = step;
    }
    if (keyword) {
      param.keyword = keyword;
    }
    if (isInUse) {
      param.isInUse = true;
    }

    setParam(param);
    history.push(`/program/course?${qs.stringify(param)}`);
  }

  const onExportTable = () => {
    setExportTableLoading(true);
    const data = _.cloneDeep(courseData);
    data.forEach((course: any) => {
      // 유형 열 변환
      course.curriculumType = course.curriculumType === 'R' ? t('program.course.column1') : course.curriculumType;
    });
    const currentDate = moment(new Date());
    exportTable(columns, data, `${t('program.course.3')}_(${currentDate.format('YYYYMMDD')})`, t('program.course.3'), [12, 15, 20, 10, 10, 15, 12, 15, 17, 10]);
    setExportTableLoading(false);
  }

  const columns: TableColumnsType<any> = [
    {
      title: t('common.label.1'),
      dataIndex: 'eduStepCode',
      align: 'center',
      render: (value) => getCommonCodeLabel('PR0006', value, i18n.language),
    },
    {
      title: t('program.course.column2'),
      dataIndex: 'levelName',
    },
    {
      title: t('program.course.4'),
      dataIndex: 'stepName',
    },
    {
      title: t('program.course.column4'),
      dataIndex: 'courseDetailName',
    },
    {
      title: t('program.course.column5'),
      dataIndex: 'curriculumType',
      render: (curriculumType: string) => {
        switch (curriculumType) {
          case 'R':
            return t('program.course.column1');
          default:
            return '';
        }
      }
    },
    {
      title: t('program.course.column6'),
      dataIndex: 'weekLectureCount',
      render: (weekLectureCount: number) => `${t('program.course.text1')} ${weekLectureCount} ${t('program.course.text2')}`
    },
    {
      title: t('program.course.column7'),
      dataIndex: 'lessonTime',
    },
    {
      title: t('program.course.column8'),
      dataIndex: 'dayLessonCount',
    },
    {
      title: t('program.course.column9'),
      dataIndex: 'onlineTuition',
      render: value => value?.toLocaleString()
    },
    {
      title: t('program.course.column10'),
      dataIndex: 'alexTuition',
      render: value => value?.toLocaleString()
    },
    {
      title: t('program.course.column11'),
      dataIndex: 'useYn',
    }
  ];

  return (
    <>
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>
              {t('common.label.1')}
            </label>
            <div>
              <SelectEduStep
                eduStepCode={eduStepCode}
                setEduStepCode={(value) => {
                  setEduStepCode(value);
                  setLevel('');
                  setStep('');
                }}
                includeAll
                fullWidth
              />
            </div>
          </li>
          <li>
            <label>
              {t('program.course.5')}
            </label>
            <div>
              <Select
                value={level}
                onChange={(value) => onSetLevel(value)}
              >
                <Select.Option value="">
                  {t('common.text.all')}
                </Select.Option>
                {getLevelOptions().map((item: any) => (
                  <Select.Option key={item.value} value={item.value}>
                    {item.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </li>
          <li>
            <label>{t('program.course.4')}</label>
            <div>
              <Select
                value={step}
                onChange={(value) => setStep(value)}
              >
                <Select.Option value="">
                  {t('common.text.all')}
                </Select.Option>
                {steps?.map((item: any) => {
                  return (
                    <Select.Option key={item.value} value={item.value}>
                      {item.label}
                    </Select.Option>
                  );
                })}
              </Select>
            </div>
          </li>
          <li>
            <input
              type="text"
              className="form-control w-240p"
              placeholder={t('program.course.placeholder1')}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
            />
            <label className="text-right">
              {t('program.course.column11')}
            </label>
            <Checkbox
              checked={isInUse}
              onChange={() => setIsInUse(!isInUse)}
              text={t('common.text.inUse')}
            />
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>
    <div className="content scrollbar">
      {(isLoading || isLoading2 || exportTableLoading) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <SimpleHeader
        parentMenu={t('common.menu.program')}
        currentMenu={t('program.course.title1')}
        iconFile="icon-program-big.svg"
        onlyTitle={true}
      />

      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">
            {t('common.text.count')}: <b>{courseData?.length}</b>
            {t('common.text.unit')}
          </div>
          <div className="comm-btn__area">
            <Button
              className="btn-basic pink"
              onClick={() =>
                history.push(`/program/course/create?${qs.stringify(param)}`)
              }
              text={t('program.award.button2')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={courseData || undefined}
            pagination={{
              current: 1,
              pageSize: courseData.length,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  history.push(
                    `/program/course/${
                      record.courseDetailCode
                    }/edit?${qs.stringify(param)}`,
                  );
                },
              };
            }}
            rowClassName="pointer"
          />
        </div>
        
        <div className="comm-bx__between">
          <div className="comm-btn__area">
            <DownloadButton onClick={onExportTable} />
          </div>
          {/* <div className="col-6">
            <Pagination
              count={courseData?.length || 0}
              page={page}
              param={param}
              path="/program/course"
            />
          </div> */}
          <div className="comm-btn__area right">
            <Button
              className="btn-basic pink"
              onClick={() =>
                history.push(`/program/course/create?${qs.stringify(param)}`)
              }
              text={t('program.award.button2')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

export default ProgramCourse;
